import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Crown, Zap, Users, Building, Tag, Loader2, ArrowRight, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";

const PLAN_ICONS = { starter: Zap, pro: Crown, team: Users, enterprise: Building };
const PLAN_COLORS = { starter: "#64748b", pro: "var(--t-primary)", team: "#7C3AED", enterprise: "#D97706" };

export const SubscriptionSection = ({ subscription, onSubscriptionChange }) => {
  const [plans, setPlans] = useState(null);
  const [billingCycle, setBillingCycle] = useState("annual");
  const [discountCode, setDiscountCode] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/plans`, { withCredentials: true });
      setPlans(res.data.plans);
    } catch { toast.error("Failed to load plans"); }
  }, []);

  const checkForStripeReturn = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    window.history.replaceState({}, "", "/settings");
    toast.info("Verifying payment...");
    for (let i = 0; i < 6; i++) {
      try {
        const res = await axios.get(`${API}/subscription/checkout-status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === "paid") {
          toast.success("Subscription activated!");
          onSubscriptionChange();
          return;
        }
        if (res.data.status === "expired") {
          toast.error("Payment session expired");
          return;
        }
      } catch (err) { console.error("Subscription status check failed:", err); }
      await new Promise((r) => setTimeout(r, 2000));
    }
    toast.error("Payment status check timed out. It may take a moment to reflect.");
    onSubscriptionChange();
  }, [onSubscriptionChange]);

  useEffect(() => {
    fetchPlans();
    checkForStripeReturn();
  }, [fetchPlans, checkForStripeReturn]);

  const handleValidateCoupon = async () => {
    if (!discountCode.trim()) return;
    try {
      const res = await axios.post(`${API}/subscription/validate-coupon`, { code: discountCode }, { withCredentials: true });
      setCouponResult(res.data);
      if (res.data.valid) toast.success(`Coupon applied: ${res.data.description}`);
      else toast.error("Invalid coupon code");
    } catch { toast.error("Failed to validate coupon"); }
  };

  const handleSubscribe = async (planId) => {
    setCheckingOut(planId);
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
        discount_code: couponResult?.valid ? couponResult.code : null,
      }, { withCredentials: true });
      if (res.data.free_activation) {
        toast.success(`${res.data.plan_name} plan activated!`);
        onSubscriptionChange();
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout");
    } finally {
      setCheckingOut(null);
    }
  };

  return (
    <Card className="card-surface" data-testid="subscription-card" id="subscription-section">
      <CardHeader style={{ borderBottom: '1px solid var(--t-border)', padding: '16px 20px' }}>
        <div className="flex items-center gap-3">
          <Crown className="w-5 h-5" style={{ color: 'var(--t-primary)' }} />
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold" style={{ color: 'var(--t-text)', fontFamily: 'var(--t-heading-font)' }}>
              Subscription & Plans
            </CardTitle>
            <CardDescription style={{ color: 'var(--t-text-muted)' }}>
              {subscription ? `Current plan: ${subscription.plan_name || "Starter"}` : "Manage your subscription"}
            </CardDescription>
          </div>
          {subscription?.plan_id && subscription.plan_id !== "starter" && (
            <Badge className="text-xs" style={{ background: PLAN_COLORS[subscription.plan_id] + '20', color: PLAN_COLORS[subscription.plan_id], border: `1px solid ${PLAN_COLORS[subscription.plan_id]}30` }}>
              {subscription.plan_name} Plan
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-6">
        {/* Billing Cycle Toggle */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billingCycle === "monthly" ? "text-white shadow-md" : ""}`}
            style={billingCycle === "monthly" ? { background: 'var(--t-primary)' } : { color: 'var(--t-text-muted)', background: 'var(--t-bg)' }}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billingCycle === "annual" ? "text-white shadow-md" : ""}`}
            style={billingCycle === "annual" ? { background: 'var(--t-primary)' } : { color: 'var(--t-text-muted)', background: 'var(--t-bg)' }}
            data-testid="billing-annual"
          >
            Annual
            <span className="ml-1.5 text-[10px] font-bold opacity-80">SAVE 17%</span>
          </button>
        </div>

        {/* Plans Grid */}
        {plans && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(plans).map(([id, plan]) => {
              const isCurrent = (subscription?.plan_id || "starter") === id;
              const Icon = PLAN_ICONS[id] || Zap;
              const color = PLAN_COLORS[id] || "var(--t-primary)";
              const price = billingCycle === "annual" ? plan.annual_price : plan.monthly_price;
              const discounted = couponResult?.valid ? price * (1 - couponResult.percent_off / 100) : price;
              const isEnterprise = id === "enterprise";

              return (
                <div
                  key={id}
                  className={`rounded-xl border-2 p-5 transition-all relative ${isCurrent ? "shadow-lg" : "hover:shadow-md"}`}
                  style={{
                    borderColor: isCurrent ? color : 'var(--t-border)',
                    background: isCurrent ? color + '08' : 'var(--t-surface, white)',
                  }}
                  data-testid={`plan-card-${id}`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: color }}>
                      Current Plan
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + '15' }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm" style={{ color: 'var(--t-text)' }}>{plan.name}</h3>
                      <p className="text-[11px]" style={{ color: 'var(--t-text-muted)' }}>{plan.tagline}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    {isEnterprise ? (
                      <p className="text-lg font-bold" style={{ color: 'var(--t-text)' }}>Custom Pricing</p>
                    ) : price === 0 ? (
                      <p className="text-2xl font-bold" style={{ color: 'var(--t-text)' }}>Free</p>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        {couponResult?.valid && discounted !== price && (
                          <span className="text-sm line-through" style={{ color: 'var(--t-text-muted)' }}>${price}</span>
                        )}
                        <span className="text-2xl font-bold" style={{ color: 'var(--t-text)' }}>
                          ${discounted % 1 === 0 ? discounted : discounted.toFixed(2)}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                          /{billingCycle === "annual" ? "yr" : "mo"}
                        </span>
                      </div>
                    )}
                    {billingCycle === "annual" && !isEnterprise && price > 0 && (
                      <p className="text-[11px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>
                        ~${Math.round((couponResult?.valid ? discounted : price) / 12)}/mo billed annually
                      </p>
                    )}
                  </div>

                  <ul className="space-y-1.5 mb-4">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs" style={{ color: 'var(--t-text)' }}>
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color }} />
                        <span>
                          {f.replace(" (Coming Soon)", "")}
                          {f.includes("(Coming Soon)") && (
                            <span className="ml-1 text-[9px] uppercase tracking-wider font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">Coming Soon</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <Button variant="outline" size="sm" className="w-full" disabled data-testid={`plan-btn-${id}`}>
                      <Check className="w-4 h-4 mr-1" /> Current Plan
                    </Button>
                  ) : isEnterprise ? (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => window.open("mailto:support@mattcteam.com?subject=Enterprise%20Plan%20Inquiry", "_blank")} data-testid={`plan-btn-${id}`}>
                      Contact Us <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  ) : id === "starter" ? (
                    <Button variant="outline" size="sm" className="w-full" disabled={isCurrent} data-testid={`plan-btn-${id}`}>
                      Free Forever
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="w-full text-white"
                      style={{ background: color }}
                      onClick={() => handleSubscribe(id)}
                      disabled={checkingOut === id}
                      data-testid={`plan-btn-${id}`}
                    >
                      {checkingOut === id ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing...</> : <>Upgrade <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Coupon Code */}
        <Separator />
        <div className="flex items-center gap-3">
          <Tag className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--t-text-muted)' }} />
          <Input
            placeholder="Discount code"
            value={discountCode}
            onChange={(e) => { setDiscountCode(e.target.value); setCouponResult(null); }}
            className="max-w-[200px] h-8 text-sm"
            data-testid="discount-code-input"
          />
          <Button variant="outline" size="sm" onClick={handleValidateCoupon} data-testid="apply-coupon-btn">
            Apply
          </Button>
          {couponResult && (
            <span className={`text-xs font-medium ${couponResult.valid ? "text-green-600" : "text-red-500"}`}>
              {couponResult.valid ? `${couponResult.percent_off}% off applied` : "Invalid code"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
